import type {
  DragonTile,
  Frame,
  GetFramesResponse,
  SiteId,
} from "@/types/dragon-tiles";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";

function patchFrameResults(
  results: Frame[],
  resolveFrame: (frame: Frame) => Frame | undefined,
): { results: Frame[]; mutated: boolean } {
  let mutated = false;
  const next = results.map((frame) => {
    const updated = resolveFrame(frame);
    if (!updated) return frame;
    mutated = true;
    return updated;
  });
  return { results: next, mutated };
}

/** Patches frame entries across paginated and infinite `["frames", siteId, …]` caches. */
export function patchFramesInFramesQueries(
  queryClient: QueryClient,
  siteId: SiteId,
  resolveFrame: (frame: Frame) => Frame | undefined,
): void {
  queryClient.setQueriesData(
    { queryKey: ["frames", siteId] },
    (oldData: unknown) => {
      if (!oldData || typeof oldData !== "object") return oldData;

      if ("pages" in oldData && Array.isArray(oldData.pages)) {
        const infiniteData = oldData as InfiniteData<GetFramesResponse>;
        let anyMutated = false;
        const pages = infiniteData.pages.map((page) => {
          if (!page?.results) return page;
          const { results, mutated } = patchFrameResults(
            page.results,
            resolveFrame,
          );
          if (!mutated) return page;
          anyMutated = true;
          return { ...page, results };
        });
        return anyMutated ? { ...infiniteData, pages } : oldData;
      }

      if ("results" in oldData && Array.isArray(oldData.results)) {
        const paginatedData = oldData as GetFramesResponse;
        const { results, mutated } = patchFrameResults(
          paginatedData.results,
          resolveFrame,
        );
        return mutated ? { ...paginatedData, results } : oldData;
      }

      return oldData;
    },
  );
}

// Template exclusion constants - these frames should not appear in user-facing lists
export const QUEEN_ONE_SITE_ID = "oaAgVjL";
const EXCLUDED_FRAME_SLUGS = new Set(["blank-template", "broadcast-template"]);

/** The starter frames new emails are built from, not real templates. */
export const isStarterTemplateFrame = ({ slug }: { slug: string }) =>
  EXCLUDED_FRAME_SLUGS.has(slug);

/**
 * Filters out internal template frames from results.
 * Queen One site is exempt from filtering (sees all frames).
 */
export const filterExcludedFrames = <T extends Frame[]>(
  frames: T,
  siteId: SiteId,
): T => {
  if (siteId === QUEEN_ONE_SITE_ID) {
    return frames;
  }
  return frames.filter((frame) => !isStarterTemplateFrame(frame)) as T;
};

/**
 * Inner tile id for creating Broadcast SMS frames from the text composer.
 * Uses the same parent as email broadcast lists (`slug === "broadcast"` on GET …/dragon-tiles),
 * then the lowest-`order` inner tile. Falls back to `name === "Broadcast"` when `slug` is absent.
 */
export function getBroadcastInnerTileIdFromDragonTiles(
  tiles: DragonTile[],
): string | undefined {
  const broadcastParent =
    tiles.find((tile) => tile.slug === "broadcast") ??
    tiles.find((tile) => tile.name === "Broadcast");
  if (!broadcastParent?.inner_tiles.length) {
    return undefined;
  }
  const [firstByOrder] = [...broadcastParent.inner_tiles].sort(
    (a, b) => a.order - b.order,
  );
  return firstByOrder?.id;
}
